import React, { useState, useEffect } from 'react';
import { Drawer, Table, Group, Text, Button, UnstyledButton } from '@mantine/core';
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import CampaignReport from "../../api/CampaignReport";
import { VisiLoader } from "../../components/GeneralComponent/GeneralComponent";

export function ReportKeywordDrawer({ selectedAdsetName, selectedAdsetId, selectedAdsetCampaignid, showKeywordDrawer, setShowKeywordDrawer, reportTimeValue }) {
  
  const navigate = useNavigate();
  const [isVisiLoaderVisible, setIsVisiLoaderVisible] = useState(false);
  const [keywordData, setKeywordData] = useState([]);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    if (showKeywordDrawer && selectedAdsetName && selectedAdsetCampaignid) {
      getKeywordDrawerData();
    }
  }, [showKeywordDrawer, selectedAdsetName, selectedAdsetCampaignid]);

  const getKeywordDrawerData = () => {
    setIsVisiLoaderVisible(true);
    CampaignReport.getkeyworddrawerdata({
      campaign_id: selectedAdsetCampaignid,
      adset_name: selectedAdsetName,
      adset_id: selectedAdsetId,
      report_time: reportTimeValue
    })
    .then(response => {
      if (response.ok) return response.json();

      if (response.status === 401 || response.status === 400) {
        navigate('/logout');
      }
    })
    .then(data => {
      if (data?.status === 1) {
        setKeywordData(data.data.keyword_data || []);
      } else {
        setKeywordData([]);
      }
    })
    .finally(() => {
      setIsVisiLoaderVisible(false);
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortKey === key && sortDirection === 'asc') {
      direction = 'desc';
    }
    setSortKey(key);
    setSortDirection(direction);
    const sorted = [...keywordData].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (typeof aVal === 'string') {
        return direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return direction === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    });
    setKeywordData(sorted);
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) {
      return <IconChevronUp size={14} color="#adb5bd" />;
    }
    return sortDirection === 'asc' ? (
      <IconChevronUp size={14} color="#212529" />
    ) : (
      <IconChevronDown size={14} color="#212529" />
    );
  };

  const exportKeywords = () => {
    const headers = ['Keyword', 'Match Type', 'Impressions', 'Clicks', 'Spend'];
    const rows = keywordData.map(row => [
      row.keyword,
      row.match_type,
      row.impressions,
      row.clicks,
      row.spend,
    ]);
    // Match the drawer's on-screen Total row so the exported file is self-checking too.
    const totalRow = ['Total', '', totals.impressions, totals.clicks, totals.spend.toFixed(2)];
    const csvContent = [headers, ...rows, totalRow]
      .map(e => e.join(','))
      .join('\n');
    const safeAdsetName = (selectedAdsetName || 'keywords')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const fileName = `keywords-${safeAdsetName}.csv`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Totals so the user can match the drawer against the traffic-source row at a glance,
  // instead of adding up each keyword by hand.
  const totals = keywordData.reduce(
    (acc, row) => {
      acc.impressions += Number(row.impressions) || 0;
      acc.clicks += Number(row.clicks) || 0;
      acc.spend += Number(row.spend) || 0;
      return acc;
    },
    { impressions: 0, clicks: 0, spend: 0 }
  );

  return (
    <Drawer
      opened={showKeywordDrawer}
      onClose={() => setShowKeywordDrawer(false)}
      position="right"
      size="xl"
      withCloseButton={false}
    >
      <Drawer.Header>
        <Group w="100%" position="apart" align="center">
          <Drawer.Title>
            Keyword View – <Text span fw={600}>{selectedAdsetName}</Text>
          </Drawer.Title>
          <Group spacing="xs">
            <Button size="sm" variant="light" onClick={exportKeywords}>
              Export
            </Button>
            <Drawer.CloseButton />
          </Group>
        </Group>
      </Drawer.Header>
      {isVisiLoaderVisible ? (
        <VisiLoader VisiLoaderVisible={isVisiLoaderVisible}/>
      ) : (
        <>
          <Table striped>
            <thead>
              <tr>
                <th>
                  <UnstyledButton onClick={() => handleSort('keyword')}>
                    <Group spacing={4} align="center">
                      <Text size="sm" fw={500}>Keyword</Text>
                      <SortIcon column="keyword" />
                    </Group>
                  </UnstyledButton>
                </th>
                <th>
                  <UnstyledButton onClick={() => handleSort('match_type')}>
                    <Group spacing={4} align="center">
                      <Text size="sm" fw={500}>Match Type</Text>
                      <SortIcon column="match_type" />
                    </Group>
                  </UnstyledButton>
                </th>
                <th>
                  <UnstyledButton onClick={() => handleSort('impressions')}>
                    <Group spacing={4} align="center">
                      <Text size="sm" fw={500}>Impressions</Text>
                      <SortIcon column="impressions" />
                    </Group>
                  </UnstyledButton>
                </th>
                <th>
                  <UnstyledButton onClick={() => handleSort('clicks')}>
                    <Group spacing={4} align="center">
                      <Text size="sm" fw={500}>Clicks</Text>
                      <SortIcon column="clicks" />
                    </Group>
                  </UnstyledButton>
                </th>
                <th>
                  <UnstyledButton onClick={() => handleSort('spend')}>
                    <Group spacing={4} align="center">
                      <Text size="sm" fw={500}>Spend</Text>
                      <SortIcon column="spend" />
                    </Group>
                  </UnstyledButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {keywordData.length > 0 ? (
                keywordData.map((row, index) => (
                  <tr key={`${row.keyword}-${row.match_type}-${index}`}>
                    <td>{row.keyword}</td>
                    <td>{row.match_type}</td>
                    <td>{row.impressions}</td>
                    <td>{row.clicks}</td>
                    <td>${row.spend}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center' }}>
                    No keywords found
                  </td>
                </tr>
              )}
            </tbody>
            {keywordData.length > 0 && (
              <tfoot>
                <tr style={{ borderTop: '2px solid #dee2e6' }}>
                  <td><Text size="sm" fw={700}>Total</Text></td>
                  <td></td>
                  <td><Text size="sm" fw={700}>{totals.impressions.toLocaleString()}</Text></td>
                  <td><Text size="sm" fw={700}>{totals.clicks.toLocaleString()}</Text></td>
                  <td><Text size="sm" fw={700}>${totals.spend.toFixed(2)}</Text></td>
                </tr>
              </tfoot>
            )}
          </Table>
        </>
      )}
    </Drawer>
  );
}